import React, { useMemo, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { oeffneNotizen } from '../../lib/nav';
import { schuljahrFenster } from '../../lib/schuljahr';
import { Notiz, katInfo, notizText, ttmm } from './notizen-lib';
import { NotizSheet } from './NotizSheet';
import './Notizen.css';

// Welle U4b: Profil › Überblick — Notizen aus der Datenbank (ersetzt den Sitzungsspeicher).
// Angepinnte zuerst (Tint), dann laufendes Schuljahr (max. 4), ältere Schuljahre eingeklappt.
// Welle P1b: fehler ≠ leer (mit „Erneut versuchen“); leer heißt: nichts Angepinntes und nichts im laufenden Schuljahr.
export function NotizenProfilKarte({ schuelerId, klasseId, vorname, notizen, fehler = false, onErneut }: {
  schuelerId: number; klasseId: number | null; vorname: string; notizen: Notiz[] | null;
  fehler?: boolean; onErneut?: () => void;
}) {
  const [sheet, setSheet] = useState<{ notiz: Notiz | null } | null>(null);
  const [offenSj, setOffenSj] = useState<string | null>(null);
  const sj = schuljahrFenster().label;
  const liste = useMemo(() => notizen ?? [], [notizen]);
  const angepinnt = liste.filter(n => n.angepinnt);
  const aktuell = liste.filter(n => !n.angepinnt && n.schuljahr === sj);
  const aeltere = useMemo(() => {
    const m = new Map<string, Notiz[]>();
    for (const n of liste.filter(x => !x.angepinnt && x.schuljahr !== sj)) {
      const k = n.schuljahr ?? 'ohne Datum';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(n);
    }
    return Array.from(m.entries()).sort(([a], [b]) => b.localeCompare(a));
  }, [liste, sj]);

  const zeile = (n: Notiz) => {
    const k = katInfo(n.kategorie);
    return (
      <button key={n.id} className="ld-nzp-zeile" onClick={() => setSheet({ notiz: n })} aria-label={`Notiz vom ${ttmm(n.datum)} öffnen`}>
        <span className="ld-nzp-datum">{ttmm(n.datum)}</span>
        <span className="ld-nzp-text">
          <span className="ld-nzp-kat" title={k.label}><i style={{ background: k.punkt ?? 'var(--ld-text-tertiary)' }} aria-hidden="true" />{k.label}</span>
          {notizText(n)}
        </span>
      </button>
    );
  };

  return (
    <div className="ld-nzp">
      <div className="ld-nzp-kopf">
        <MIcon name="sticky_note_2" size={18} />
        <h3>Notizen</h3>
        {/* Welle P1d: neue Notiz über „Eintragen“ (PC-Kopf) bzw. die Leiste unten — hier kein zweiter Weg */}
      </div>

      {fehler && notizen === null ? (
        <div className="ld-nzp-leer" role="alert">
          Konnte nicht geladen werden.{' '}
          {onErneut && <button type="button" className="ld-nzp-neu" onClick={onErneut}><MIcon name="refresh" size={15} />Erneut versuchen</button>}
        </div>
      ) : notizen === null ? (
        <div className="ld-nzp-leer">Lade Notizen …</div>
      ) : (
        <>
          {angepinnt.length === 0 && aktuell.length === 0 && (
            <div className="ld-nzp-leer">
              Noch keine Notiz in diesem Schuljahr.
            </div>
          )}
          {angepinnt.map(n => (
            <button key={n.id} className="ld-nzp-pin" onClick={() => setSheet({ notiz: n })} aria-label={`Angepinnte Notiz vom ${ttmm(n.datum)} öffnen`}>
              <MIcon name="push_pin" size={16} fill />
              <span>{notizText(n)}</span>
            </button>
          ))}
          {aktuell.length > 0 && <div className="ld-nzp-abschnitt">Schuljahr {sj}</div>}
          {aktuell.slice(0, 4).map(zeile)}
          {aeltere.map(([label, rows]) => (
            <div key={label} className="ld-nzp-aelter">
              <button className="ld-nzp-klapp" aria-expanded={offenSj === label} onClick={() => setOffenSj(offenSj === label ? null : label)}>
                <MIcon name="history" size={16} />
                <span>Schuljahr {label} · {rows.length} {rows.length === 1 ? 'Notiz' : 'Notizen'}</span>
                <MIcon name={offenSj === label ? 'expand_less' : 'expand_more'} size={18} />
              </button>
              {offenSj === label && rows.map(zeile)}
            </div>
          ))}
        </>
      )}

      {liste.length > 0 && (
        <button className="ld-nzp-alle" onClick={() => oeffneNotizen(schuelerId, klasseId)}>
          Alle {liste.length} Notizen von {vorname} <MIcon name="arrow_forward" size={16} />
        </button>
      )}

      {sheet && <NotizSheet notiz={sheet.notiz} vorbelegtSchuelerId={schuelerId} onClose={() => setSheet(null)} />}
    </div>
  );
}
